"use client";

import type { Session, SupabaseClient } from "@supabase/auth-helpers-nextjs";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import { useRouter } from "@/navigation";
import { UserSettings } from "@/types";
import type { Database } from "@/types_db";
import { setLocalUserSettings } from "@/utils/helpers";
import { getUserSettings } from "@/utils/supabase-client";
import { createClient } from "@/utils/supabase/client";

type SupabaseContext = {
    supabase: SupabaseClient<Database>;
    userSettings?: UserSettings;
};

const Context = createContext<SupabaseContext | undefined>(undefined);

export default function SupabaseProvider({
    children
}: {
    children: React.ReactNode;
}) {
    const [supabase] = useState(() => createClient());
    const [userSettings, setUserSettings] = useState<UserSettings>();
    const router = useRouter();
    const sessionRef = useRef<Session | null>(null);

    useEffect(() => {
        async function updateUserSettings() {
            if (sessionRef.current) {
                return;
            }
            const {
                data: { session }
            } = await supabase.auth.getSession();

            if (!session) {
                return;
            }

            sessionRef.current = session;

            const userSettings = await getUserSettings();
            if (userSettings) {
                setLocalUserSettings(userSettings);
                setUserSettings(userSettings);
                router.refresh();
            }
        }
        const {
            data: { subscription }
        } = supabase.auth.onAuthStateChange((event) => {
            if (event === "SIGNED_OUT") {
                sessionRef.current = null;
            }
            updateUserSettings().catch((error) => {
                console.log("Error updating user settings", error);
            });
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [router, supabase]);

    return (
        <Context.Provider value={{ supabase, userSettings }}>
            <>{children}</>
        </Context.Provider>
    );
}

export const useSupabase = () => {
    const context = useContext(Context);

    if (context === undefined) {
        throw new Error("useSupabase must be used inside SupabaseProvider");
    }

    return context;
};
